/**
 * Pure shorthand line parser — inverse of backend OptionTypeShorthandCalculator.
 * Never throws. grammarPack from GET /shorthand-grammar.
 */

const MONTHS = {
  jan: "Jan",
  feb: "Feb",
  mar: "Mar",
  apr: "Apr",
  may: "May",
  jun: "Jun",
  jul: "Jul",
  aug: "Aug",
  sep: "Sep",
  oct: "Oct",
  nov: "Nov",
  dec: "Dec",
};

const SEASON_RE = /^(sum|win|cal|q[1-4])-?(\d{2})$/i;
const MONTH_RE = /^(jan|feb|mar|apr|may|jun|jul|aug|sep|oct|nov|dec)-?(\d{2})$/i;

const emptyResult = (overrides = {}) => ({
  ok: false,
  contract: null,
  tenors: [],
  label: null,
  optionTypeId: null,
  optionTypeName: null,
  strikes: [],
  ratio: [],
  underlying: null,
  live: null,
  sides: { buy: false, sell: false },
  prices: [],
  vols: [],
  legs: [],
  assumed: [],
  missing: [],
  namingFamily: null,
  ...overrides,
});

const trimLabel = (s) => String(s || "").trim().replace(/^\s+/, "");

const MONTH_INDEX = {
  jan: 0, feb: 1, mar: 2, apr: 3, may: 4, jun: 5,
  jul: 6, aug: 7, sep: 8, oct: 9, nov: 10, dec: 11,
};

/**
 * True when a normalized tenor ("Apr-26", "Q1-25", "Sum-25", "Win-25",
 * "Cal-25") ends before the current month — likely an expired contract.
 */
export function isTenorPast(tenor, now = new Date()) {
  const m = String(tenor || "").match(/^([A-Za-z]{3}|Q[1-4])-(\d{2})$/i);
  if (!m) return false;
  const head = m[1].toLowerCase();
  const year = 2000 + Number(m[2]);
  let endMonth;
  let endYear = year;
  if (head in MONTH_INDEX) {
    endMonth = MONTH_INDEX[head];
  } else if (head.startsWith("q")) {
    endMonth = Number(head[1]) * 3 - 1;
  } else if (head === "sum") {
    endMonth = 8; // Apr–Sep
  } else if (head === "win") {
    endMonth = 2; // Oct–Mar of next year
    endYear = year + 1;
  } else if (head === "cal") {
    endMonth = 11;
  } else {
    return false;
  }
  return (
    endYear < now.getFullYear() ||
    (endYear === now.getFullYear() && endMonth < now.getMonth())
  );
}

// grammarPack.expiries: { [contractToken]: { [tenor]: "YYYY-MM-DD" } }.
// Flattened to a case-insensitive Map once per pack.
const EXPIRY_INDEX_CACHE = new WeakMap();

function expiryLookup(grammarPack) {
  if (!grammarPack?.expiries) return null;
  let index = EXPIRY_INDEX_CACHE.get(grammarPack);
  if (!index) {
    index = new Map();
    Object.entries(grammarPack.expiries).forEach(([token, tenors]) => {
      Object.entries(tenors || {}).forEach(([tenor, date]) => {
        index.set(`${token}|${tenor}`.toLowerCase(), date);
      });
    });
    EXPIRY_INDEX_CACHE.set(grammarPack, index);
  }
  return index;
}

/**
 * Expiry check for a normalized tenor. Uses the exact option expiry date from
 * grammarPack.expiries when the backend provides it; otherwise falls back to
 * the isTenorPast month-end heuristic.
 */
export function isTenorExpired(
  tenor,
  { grammarPack, contractToken, now = new Date() } = {}
) {
  const index = expiryLookup(grammarPack);
  if (index && contractToken) {
    const iso = index.get(`${contractToken}|${tenor}`.toLowerCase());
    if (iso) {
      const expiry = new Date(`${iso}T23:59:59`);
      if (!Number.isNaN(expiry.getTime())) return expiry < now;
    }
  }
  return isTenorPast(tenor, now);
}

// Label index is derived only from the grammar pack; cache it so debounced
// keystrokes don't rebuild + resort all labels on every parse.
const LABEL_INDEX_CACHE = new WeakMap();

export function buildLabelIndex(grammarPack) {
  const cached = LABEL_INDEX_CACHE.get(grammarPack);
  if (cached) return cached;
  const labels = new Map();
  const add = (raw, meta) => {
    const key = trimLabel(raw).toLowerCase();
    if (!key) return;
    if (!labels.has(key) || key.length > (labels.get(key).key?.length || 0)) {
      labels.set(key, { key, ...meta });
    }
  };

  (grammarPack.constraints || []).forEach((c) => {
    add(c.shortname, {
      source: "constraint",
      optionTypeId: Number(c.option_type_id),
      shortname: trimLabel(c.shortname),
      constraint: c,
    });
  });

  (grammarPack.shorthands || []).forEach((s) => {
    add(s.short_name, {
      source: "shorthand",
      optionTypeId: Number(s.option_type_id),
      shortname: trimLabel(s.short_name),
      shorthand: s,
      namingFamily: String(s.naming_shorthand || "").startsWith("E4")
        ? "E4"
        : String(s.naming_shorthand || "").startsWith("E2")
          ? "E2"
          : "B",
    });
  });

  // Full option-type names too: "call spread", "callspread" (spaces stripped).
  (grammarPack.optionTypes || []).forEach((ot) => {
    const name = trimLabel(ot.name);
    if (!name) return;
    const id = Number(ot.id);
    const constraint = (grammarPack.constraints || []).find(
      (c) => Number(c.option_type_id) === id
    );
    const shortname = constraint
      ? trimLabel(constraint.shortname)
      : name;
    const meta = {
      source: "optionType",
      optionTypeId: id,
      shortname,
    };
    add(name, meta);
    const compact = name.replace(/\s+/g, "");
    if (compact.toLowerCase() !== name.toLowerCase()) {
      add(compact, meta);
    }
  });

  // Longest label first for matching.
  const index = [...labels.values()].sort((a, b) => b.key.length - a.key.length);
  LABEL_INDEX_CACHE.set(grammarPack, index);
  return index;
}

export function findContract(headToken, grammarPack) {
  const token = String(headToken || "").trim();
  if (!token) return null;
  const lower = token.toLowerCase();
  const contracts = grammarPack.contracts || [];
  // Exact token first so "API2" does not resolve to "API2 (APO)" via alias.
  for (const c of contracts) {
    if (String(c.token).toLowerCase() === lower) return c;
  }
  for (const c of contracts) {
    const aliases = c.aliases || [];
    if (aliases.some((a) => String(a).toLowerCase() === lower)) return c;
  }
  // Commodity-name fallback: if user typed commodity and exactly one contract shares it.
  const byCommodity = contracts.filter(
    (c) => String(c.commodity || "").toLowerCase() === lower
  );
  if (byCommodity.length === 1) return byCommodity[0];
  return null;
}

function normalizeTenorBase(raw) {
  const t = String(raw || "")
    .trim()
    .replace(/:+$/, "");
  if (!t) return null;
  let m = t.match(MONTH_RE);
  if (m) return `${MONTHS[m[1].toLowerCase()]}-${m[2]}`;
  m = t.match(SEASON_RE);
  if (m) {
    const season = m[1].toLowerCase();
    const yy = m[2];
    if (season.startsWith("q")) return `Q${season.slice(1)}-${yy}`;
    if (season === "sum") return `Sum-${yy}`;
    if (season === "win") return `Win-${yy}`;
    if (season === "cal") return `Cal-${yy}`;
  }
  m = t.match(/^(Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)-(\d{2})$/i);
  if (m) {
    return `${m[1][0].toUpperCase()}${m[1].slice(1).toLowerCase()}-${m[2]}`;
  }
  return null;
}

export function normalizeTenorToken(raw) {
  // Generator often emits a trailing ":" after tenor (component tenor_suffix).
  const t = String(raw || "")
    .trim()
    .replace(/:+$/, "");
  if (!t) return null;
  // Cal-24 (Mar) / Cal-24(Mar)
  const withQual = t.match(
    /^((?:Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec|Sum|Win|Cal|Q[1-4])-?\d{2})\s*(\([^)]+\))$/i
  );
  if (withQual) {
    const base = normalizeTenorBase(withQual[1]);
    return base ? `${base} ${withQual[2]}` : null;
  }
  // Dec-23,Feb-24 (single bespoke tenor token)
  if (t.includes(",")) {
    const parts = t.split(",").map((p) => normalizeTenorBase(p.trim()));
    if (parts.length >= 2 && parts.every(Boolean)) return parts.join(",");
  }
  return normalizeTenorBase(t);
}

/** True when a slash token is Dec-26/Jun-26 (tenors), not 12/24 (strikes). */
function isSlashJoinedTenors(rawTok) {
  const parts = String(rawTok || "")
    .replace(/:+$/, "")
    .split("/");
  if (parts.length < 2 || parts.length > 2) return false;
  return parts.every((p) => !!normalizeTenorToken(p));
}

/**
 * Pull up to 2 tenors from anywhere in the token list.
 * Leaves strike tokens like 12/24cs untouched.
 */
function extractTenorsAnywhere(tokens, max = 2) {
  const tenors = [];
  const kept = [];
  for (const tok of tokens) {
    const rawTok = String(tok).replace(/:+$/, "");
    if (tenors.length < max && isSlashJoinedTenors(rawTok)) {
      const parts = rawTok.split("/").map((p) => normalizeTenorToken(p));
      for (const p of parts) {
        if (tenors.length < max) tenors.push(p);
      }
      continue;
    }
    const n = normalizeTenorToken(rawTok);
    if (n && tenors.length < max) {
      tenors.push(n);
      continue;
    }
    kept.push(tok);
  }
  return { tenors, tokens: kept };
}

function tokensMatchParts(tokens, start, parts) {
  if (start + parts.length > tokens.length) return false;
  for (let j = 0; j < parts.length; j++) {
    if (String(tokens[start + j]).toLowerCase() !== parts[j].toLowerCase()) {
      return false;
    }
  }
  return true;
}

/** Contract may be multi-token (`API2 (APO)`), first token, or anywhere. */
function extractContractAnywhere(tokens, grammarPack) {
  if (!tokens.length) return { contract: null, tokens };
  const contracts = grammarPack.contracts || [];
  const matchers = contracts
    .map((c) => ({
      contract: c,
      parts: String(c.token || "")
        .trim()
        .split(/\s+/)
        .filter(Boolean),
    }))
    .filter((m) => m.parts.length > 0)
    .sort((a, b) => b.parts.length - a.parts.length);

  // Prefer longer exact token matches from the start, then anywhere.
  for (const m of matchers) {
    if (tokensMatchParts(tokens, 0, m.parts)) {
      return { contract: m.contract, tokens: tokens.slice(m.parts.length) };
    }
  }
  for (let i = 0; i < tokens.length; i++) {
    for (const m of matchers) {
      if (tokensMatchParts(tokens, i, m.parts)) {
        return {
          contract: m.contract,
          tokens: [...tokens.slice(0, i), ...tokens.slice(i + m.parts.length)],
        };
      }
    }
  }

  // Alias / commodity fallback (single token).
  const first = findContract(tokens[0], grammarPack);
  if (first) {
    return { contract: first, tokens: tokens.slice(1) };
  }
  for (let i = 0; i < tokens.length; i++) {
    const c = findContract(tokens[i], grammarPack);
    if (c) {
      return {
        contract: c,
        tokens: [...tokens.slice(0, i), ...tokens.slice(i + 1)],
      };
    }
  }
  return { contract: null, tokens };
}

/** Merge `Cal-24` + `(Mar):` into `Cal-24 (Mar):`. */
function mergeTenorQualifiers(tokens) {
  const out = [];
  for (let i = 0; i < tokens.length; i++) {
    const t = tokens[i];
    const next = tokens[i + 1];
    if (
      next &&
      /^\([^)]+\):?$/.test(next) &&
      normalizeTenorBase(String(t).replace(/:$/, ""))
    ) {
      const qual = String(next).replace(/:$/, "");
      out.push(`${String(t).replace(/:$/, "")} ${qual}:`);
      i += 1;
      continue;
    }
    out.push(t);
  }
  return out;
}

function peelSide(text) {
  const m = text.match(/\s+(bid|offer|ofr|mid)\s*$/i);
  if (!m) return { text, side: null };
  return {
    text: text.slice(0, m.index).trim(),
    side: m[1].toLowerCase() === "ofr" ? "offer" : m[1].toLowerCase(),
  };
}

function peelVols(text) {
  // "(50.00%)" or "(-2.00%/51.14%)" or bare "50%"
  const m = text.match(
    /\s+\((-?\d+(?:\.\d+)?%?(?:\/-?\d+(?:\.\d+)?%?)?)\)\s*$/i
  );
  if (m) {
    const vols = m[1]
      .split("/")
      .map((v) => v.replace(/%/g, "").trim())
      .filter(Boolean);
    return { text: text.slice(0, m.index).trim(), vols };
  }
  const m2 = text.match(/\s+(-?\d+(?:\.\d+)?%(?:\/-?\d+(?:\.\d+)?%)?)\s*$/i);
  if (!m2) return { text, vols: [] };
  const vols = m2[1]
    .split("/")
    .map((v) => v.replace(/%/g, "").trim())
    .filter(Boolean);
  return { text: text.slice(0, m2.index).trim(), vols };
}

function peelPrices(text) {
  // ": 1.2/1.3" or trailing "1.2/1.3" after space when not a tenor/structure.
  let m = text.match(/:\s*(-?\d+(?:\.\d+)?(?:\/-?\d+(?:\.\d+)?)?)\s*$/);
  if (m) {
    return {
      text: text.slice(0, m.index).trim(),
      prices: m[1].split("/").map((p) => p.trim()),
      twoWay: m[1].includes("/"),
    };
  }
  m = text.match(/\s+(-?\d+(?:\.\d+)?\/-?\d+(?:\.\d+)?)\s*$/);
  if (m) {
    return {
      text: text.slice(0, m.index).trim(),
      prices: m[1].split("/").map((p) => p.trim()),
      twoWay: true,
    };
  }
  return { text, prices: [], twoWay: false };
}

function peelLiveOrUl(text) {
  // Trailing only — generator lines often end with `live` / `xN` after prices.
  // Start/middle positions are handled by extractLiveOrUlAnywhere on tokens.
  let m = text.match(/\s+live:?\s*$/i);
  if (m) {
    return { text: text.slice(0, m.index).trim(), live: 1, underlying: null };
  }
  m = text.match(/\s+x(-?\d+(?:\.\d+)?)\s*$/i);
  if (m) {
    return {
      text: text.slice(0, m.index).trim(),
      live: 0,
      underlying: m[1],
    };
  }
  return { text, live: null, underlying: null };
}

/**
 * Pull `live` or `x56` from anywhere in the token list.
 * First match sets the value; every live/xN token is stripped (extras ignored).
 */
function extractLiveOrUlAnywhere(tokens) {
  const kept = [];
  let live = null;
  let underlying = null;
  let found = false;
  for (const t of tokens) {
    if (/^live:?$/i.test(t)) {
      if (!found) {
        live = 1;
        underlying = null;
        found = true;
      }
      continue;
    }
    const ul = /^x(-?\d+(?:\.\d+)?)$/i.exec(t);
    if (ul) {
      if (!found) {
        live = 0;
        underlying = ul[1];
        found = true;
      }
      continue;
    }
    kept.push(t);
  }
  return { tokens: kept, live, underlying, found };
}

function peelRatio(text) {
  // Allow decimals: (1x1.5), (1x3x1x2)
  const m = text.match(/\s+\((\d+(?:\.\d+)?(?:x\d+(?:\.\d+)?)*)\)\s*$/i);
  if (!m) return { text, ratioParts: null };
  return {
    text: text.slice(0, m.index).trim(),
    ratioParts: m[1].split(/x/i).map((n) => n.trim()),
  };
}

function constraintRatioParts(constraint) {
  const r = String(constraint?.ratio || "").trim();
  if (!r || r === "*" || /^na$/i.test(r) || !/x/i.test(r)) return null;
  const parts = r.split(/x/i).map((n) => n.trim()).filter(Boolean);
  return parts.length ? parts : null;
}

function cleanStructureSide(sideText) {
  return String(sideText || "")
    .replace(/\s+x-?\d+(?:\.\d+)?\b/gi, " ")
    .replace(/:\s*-?\d+(?:\.\d+)?(?:\/-?\d+(?:\.\d+)?)?\s*$/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function findVsOptionTypeId(grammarPack, baseId) {
  const base = (grammarPack.optionTypes || []).find(
    (t) => Number(t.id) === Number(baseId)
  );
  if (!base?.name) return null;
  const want = `vs ${base.name}`.toLowerCase();
  const vs = (grammarPack.optionTypes || []).find(
    (t) => String(t.name || "").trim().toLowerCase() === want
  );
  return vs ? Number(vs.id) : null;
}

function findSpecialType(grammarPack, idA, idB, joiner) {
  const specials = grammarPack.specialTypes || [];
  const join = String(joiner || "").toLowerCase();

  if (join === "vs") {
    const orderedVs = specials.find(
      (s) =>
        Number(s.option_type_one_id) === idA &&
        Number(s.option_type_two_id) === idB &&
        /\bvs\b/i.test(s.name || "")
    );
    if (orderedVs) return orderedVs;

    const vsRight = findVsOptionTypeId(grammarPack, idB);
    if (vsRight != null) {
      const withVsRight = specials.find(
        (s) =>
          Number(s.option_type_one_id) === idA &&
          Number(s.option_type_two_id) === vsRight
      );
      if (withVsRight) return withVsRight;
    }

    const vsLeft = findVsOptionTypeId(grammarPack, idA);
    if (vsLeft != null) {
      const withVsLeft = specials.find(
        (s) =>
          Number(s.option_type_one_id) === idB &&
          Number(s.option_type_two_id) === vsLeft
      );
      if (withVsLeft) return withVsLeft;
    }
  }

  if (join === "+") {
    const orderedPlus = specials.find(
      (s) =>
        Number(s.option_type_one_id) === idA &&
        Number(s.option_type_two_id) === idB &&
        /\+/.test(s.name || "")
    );
    if (orderedPlus) return orderedPlus;
  }

  return (
    specials.find(
      (s) =>
        Number(s.option_type_one_id) === idA &&
        Number(s.option_type_two_id) === idB
    ) ||
    specials.find(
      (s) =>
        Number(s.option_type_one_id) === idB &&
        Number(s.option_type_two_id) === idA
    ) ||
    null
  );
}

function findConstraintForType(grammarPack, optionTypeId, preferShortname) {
  const list = (grammarPack.constraints || []).filter(
    (c) => Number(c.option_type_id) === Number(optionTypeId)
  );
  if (!list.length) return null;
  if (preferShortname) {
    const byName = list.find(
      (c) => trimLabel(c.shortname).toLowerCase() === preferShortname.toLowerCase()
    );
    if (byName) return byName;
  }
  // Prefer buy-first (buy_leg_one == 1) for ambiguous cs/ps.
  const buyFirst = list.find((c) => String(c.buy_leg_one) === "1");
  return buyFirst || list[0];
}

function optionTypeName(grammarPack, id) {
  const ot = (grammarPack.optionTypes || []).find(
    (t) => Number(t.id) === Number(id)
  );
  return ot?.name || null;
}

function legFlagsFromConstraint(constraint, legIndex) {
  const callKeys = [
    "call_leg_one",
    "call_leg_two",
    "call_leg_three",
    "call_leg_four",
  ];
  const buyKeys = [
    "buy_leg_one",
    "buy_leg_two",
    "buy_leg_three",
    "buy_leg_four",
  ];
  const call = String(constraint[callKeys[legIndex]] ?? "NA");
  const buy = String(constraint[buyKeys[legIndex]] ?? "NA");
  if (call === "NA" || buy === "NA") return null;
  return {
    cp: call === "1" ? "C" : "P",
    bs: buy === "1" ? "B" : "S",
  };
}

/**
 * E4 / bespoke legs:
 * - `70p - 60p + 55c`
 * - `Q3-23: 200c + Q4-23: 190c - Q1-24: 180p`
 * - `Cal-24 (Mar): 202c + 190c - 180c - 170p`
 * Optional per-leg UL: `200c x200.00 + … x201.00`
 */
function parseE4Legs(middle, defaultTenor) {
  const cleaned = String(middle || "").replace(/\s+/g, " ").trim();

  const re =
    /([+-])?\s*(?:((?:Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec|Sum|Win|Cal|Q[1-4])-?\d{2})(?:\s*(\([^)]+\)))?\s*:)?\s*(-?\d+(?:\.\d+)?)\s*([cp])(?:\s+x(-?\d+(?:\.\d+)?))?/gi;

  const parts = [];
  const legUnderlyings = [];
  let match;
  let first = true;
  let lastTenor = defaultTenor || null;
  while ((match = re.exec(cleaned)) !== null) {
    const sep = match[1];
    let tenor = lastTenor;
    if (match[2]) {
      const base = normalizeTenorBase(match[2]);
      tenor = match[3] && base ? `${base} ${match[3]}` : base;
      lastTenor = tenor;
    }
    const strike = match[4];
    const cp = match[5].toUpperCase() === "C" ? "C" : "P";
    const legUl = match[6] || null;
    let bs = "B";
    if (!first) {
      bs = sep === "-" ? "S" : "B";
    } else if (sep === "-") {
      bs = "S";
    }
    first = false;
    parts.push({
      strike,
      cp,
      bs,
      ratio: "1",
      tenor: tenor || defaultTenor || "",
      underlying: legUl,
    });
    legUnderlyings.push(legUl);
  }
  return { legs: parts, legUnderlyings };
}

function isE4Candidate(rest) {
  const cleaned = String(rest || "");
  if (/\svs\s/i.test(cleaned)) return false;
  const { legs } = parseE4Legs(cleaned, null);
  // Multi-leg with +/- separators (not a single vanilla option).
  return legs.length >= 2;
}

function structureFailMissing(missing, tenors, live) {
  const out = [...missing];
  if (!tenors.length && !out.includes("tenor")) out.push("tenor");
  if (live == null && !out.includes("underlying")) out.push("underlying");
  if (!out.includes("structure")) out.push("structure");
  return out;
}

function matchStructure(remainder, labelIndex) {
  const text = remainder.trim();
  const lower = text.toLowerCase();

  // Glued strikes + label: 60/55cs, 65c, 100/101/101/103iron fly
  for (const entry of labelIndex) {
    const label = entry.key;
    if (lower.endsWith(label)) {
      const before = text.slice(0, text.length - label.length).trim();
      // Allow glued: "60/55" + "cs" with no space
      const strikePart = before.replace(/\s+/g, "");
      if (!strikePart || /^(-?\d+(?:\.\d+)?)(\/(-?\d+(?:\.\d+)?))*$/.test(strikePart)) {
        const strikes = strikePart
          ? strikePart.split("/").filter(Boolean)
          : [];
        return { entry, strikes, beforeLabel: before };
      }
    }
  }

  // Space-separated label at end: "100 strad"
  for (const entry of labelIndex) {
    const re = new RegExp(
      `(?:^|\\s)(-?[\\d./]+)?\\s*${entry.key.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}$`,
      "i"
    );
    const m = text.match(re);
    if (m) {
      const strikes = m[1] ? m[1].split("/").filter(Boolean) : [];
      return { entry, strikes, beforeLabel: text.slice(0, m.index).trim() };
    }
  }

  return null;
}

/**
 * @param {string} raw
 * @param {object} grammarPack
 */
export function parseLine(raw, grammarPack) {
  if (!grammarPack) {
    return emptyResult({ missing: ["grammar"] });
  }

  let text = String(raw || "")
    .replace(/<[^>]+>/g, "")
    .replace(/\s+/g, " ")
    .trim();
  if (!text) {
    return emptyResult({ missing: ["empty"] });
  }

  const assumed = [];
  const missing = [];
  const labelIndex = buildLabelIndex(grammarPack);

  let side = null;
  let vols = [];
  let prices = [];
  let twoWay = false;
  let live = null;
  let underlying = null;
  let ratioParts = null;

  ({ text, side } = peelSide(text));
  ({ text, vols } = peelVols(text));
  ({ text, prices, twoWay } = peelPrices(text));
  // Only peel trailing `live` here. Leave `xN` in place so E4/E5 can keep
  // per-leg underlyings (trailing xN used to steal the last leg's UL).
  {
    const liveOnly = text.match(/\s+live:?\s*$/i);
    if (liveOnly) {
      live = 1;
      underlying = null;
      text = text.slice(0, liveOnly.index).trim();
    }
  }
  ({ text, ratioParts } = peelRatio(text));

  let tokens = mergeTenorQualifiers(text.split(/\s+/).filter(Boolean));
  // One token is fine — still extract contract/tenor so live hints stay accurate
  // (e.g. "EUA" → need tenor + structure, not a blanket "structure").

  // Contract first so E4 can still see per-leg `xN` tokens.
  const { contract, tokens: afterContract } = extractContractAnywhere(
    tokens,
    grammarPack
  );
  if (!contract) {
    missing.push("contract");
  }

  let rest = afterContract.join(" ").trim();
  // Ratio can sit before trailing junk that was already peeled; also allow mid.
  if (!ratioParts) {
    const midRatio = rest.match(/\s+\((\d+(?:\.\d+)?(?:x\d+(?:\.\d+)?)*)\)\s*/i);
    if (midRatio) {
      ratioParts = midRatio[1].split(/x/i).map((n) => n.trim());
      rest = `${rest.slice(0, midRatio.index)} ${rest.slice(
        midRatio.index + midRatio[0].length
      )}`.replace(/\s+/g, " ").trim();
    }
  }

  let legs = [];
  let label = null;
  let optionTypeId = null;
  let namingFamily = "B";
  let strikes = [];
  let tenors = [];
  let legUnderlyings = null;

  // E4 before E5: `200c + Q4: 190c - …` would otherwise look like a lone E5 `+`.
  const isE4 = isE4Candidate(rest);
  const e5Parts = rest.split(/\s+(\+|vs)\s+/i);
  const isE5 = !isE4 && e5Parts.length === 3;

  // For non-E4, strip every live/xN token. Explicit xN/live always wins over
  // a later inferred live flag.
  if (!isE4) {
    const ulAny = extractLiveOrUlAnywhere(
      mergeTenorQualifiers(rest.split(/\s+/).filter(Boolean))
    );
    if (ulAny.found) {
      live = ulAny.live;
      underlying = ulAny.underlying;
      rest = ulAny.tokens.join(" ").trim();
    }
  }

  if (isE4) {
    namingFamily = "E4";
    label = "bespoke";
    const bespokeConstraint = (grammarPack.constraints || []).find(
      (c) => trimLabel(c.shortname).toLowerCase() === "bespoke"
    );
    optionTypeId = bespokeConstraint
      ? Number(bespokeConstraint.option_type_id)
      : null;
    const parsedE4 = parseE4Legs(rest, null);
    legs = parsedE4.legs;
    legUnderlyings = parsedE4.legUnderlyings;
    if (legUnderlyings.some(Boolean)) {
      const firstUl = legUnderlyings.find(Boolean);
      // Per-leg xN means not live.
      live = 0;
      underlying = firstUl;
      // Fill gaps with the previous/first UL so every leg has a value.
      let fill = firstUl || null;
      legUnderlyings = legUnderlyings.map((u) => {
        if (u) {
          fill = u;
          return u;
        }
        return fill;
      });
    }
    if (ratioParts?.length) {
      legs = legs.map((l, i) => ({
        ...l,
        ratio: ratioParts[i] || l.ratio || "1",
      }));
    }
    strikes = legs.map((l) => l.strike);
    tenors = legs.map((l) => l.tenor).filter(Boolean);
    if (!legs.length) {
      return emptyResult({
        ok: false,
        contract,
        tenors,
        missing: structureFailMissing(missing, tenors, live),
      });
    }
  } else if (isE5) {
    namingFamily = "E5";
    // Pull tenors from the full rest (each side may carry its own).
    const { tenors: e5Tenors, tokens: e5Tokens } = extractTenorsAnywhere(
      mergeTenorQualifiers(rest.split(/\s+/).filter(Boolean)),
      4
    );
    tenors = e5Tenors;
    const e5Rest = e5Tokens.join(" ").trim();
    const parts = e5Rest.split(/\s+(\+|vs)\s+/i);
    const leftStruct = matchStructure(
      cleanStructureSide(parts[0] || ""),
      labelIndex
    );
    const rightStruct = matchStructure(
      cleanStructureSide(parts[2] || ""),
      labelIndex
    );
    const joiner = String(parts[1] || e5Parts[1] || "").toLowerCase();
    if (!leftStruct || !rightStruct) {
      return emptyResult({
        ok: false,
        contract,
        tenors,
        missing: structureFailMissing(missing, tenors, live),
      });
    }
    const idA = leftStruct.entry.optionTypeId;
    const idB = rightStruct.entry.optionTypeId;
    const special = findSpecialType(grammarPack, idA, idB, joiner);
    const specialOptionType = (grammarPack.optionTypes || []).find(
      (t) =>
        (t.is_special === true || t.is_special === 1) &&
        Number(t.option_special_type_id) === Number(special?.id)
    );
    optionTypeId = specialOptionType ? Number(specialOptionType.id) : null;
    label =
      specialOptionType?.name ||
      special?.name ||
      `${leftStruct.entry.shortname} ${joiner} ${rightStruct.entry.shortname}`;

    const expandSide = (struct, tenor, flipBs, ratioOffset) => {
      const c =
        findConstraintForType(
          grammarPack,
          struct.entry.optionTypeId,
          struct.entry.shortname
        ) || findConstraintForType(grammarPack, struct.entry.optionTypeId, null);
      if (!c) return [];
      const nLegs = Number(c.legs) || struct.strikes.length || 1;
      const out = [];
      for (let i = 0; i < nLegs; i++) {
        const flags = legFlagsFromConstraint(c, i);
        if (!flags) continue;
        let bs = flags.bs;
        if (flipBs) bs = bs === "B" ? "S" : "B";
        out.push({
          strike:
            struct.strikes[i] != null
              ? struct.strikes[i]
              : struct.strikes[0] || "",
          cp: flags.cp,
          bs,
          ratio: ratioParts?.[ratioOffset + i] || "1",
          tenor: tenor || tenors[0] || "",
        });
      }
      return out;
    };

    const leftLegs = expandSide(leftStruct, tenors[0], false, 0);
    legs = [
      ...leftLegs,
      ...expandSide(
        rightStruct,
        tenors[1] || tenors[0],
        joiner === "vs",
        leftLegs.length
      ),
    ];
    strikes = legs.map((l) => l.strike);
    if (!legs.length || !optionTypeId) {
      return emptyResult({
        ok: false,
        contract,
        tenors,
        label,
        missing: structureFailMissing(missing, tenors, live),
      });
    }
  } else {
    const extracted = extractTenorsAnywhere(
      mergeTenorQualifiers(rest.split(/\s+/).filter(Boolean)),
      2
    );
    tenors = extracted.tenors;
    rest = extracted.tokens.join(" ").trim();
    const struct = matchStructure(rest, labelIndex);
    if (!struct) {
      return emptyResult({
        ok: false,
        contract,
        tenors,
        missing: structureFailMissing(missing, tenors, live),
        assumed,
      });
    }
    label = struct.entry.shortname;
    optionTypeId = struct.entry.optionTypeId;
    strikes = struct.strikes;
    namingFamily = struct.entry.namingFamily || "B";
  }

  if (!tenors.length) {
    missing.push("tenor");
  }

  // Some live exports omit the `live` token and only keep premium + bid/offer.
  if (live == null && prices.length && side) {
    live = 1;
  }

  // Underlying required: `x56` / `x56.5` or `live` (may appear anywhere).
  if (live == null) {
    missing.push("underlying");
  }

  // Resolve constraint for leg expansion (fc → option_type_id, constraints may say rr)
  const constraint =
    findConstraintForType(grammarPack, optionTypeId, label) ||
    findConstraintForType(grammarPack, optionTypeId, null);

  if (constraint && !legs.length) {
    const nLegs = Number(constraint.legs) || strikes.length || 1;
    const ratios =
      ratioParts && ratioParts.length
        ? ratioParts
        : constraintRatioParts(constraint) ||
          Array.from({ length: nLegs }, () => "1");

    // Default buy-first. `offer` / `mid` flips to sell-first when that variant exists.
    const variants = (grammarPack.constraints || []).filter(
      (c) => Number(c.option_type_id) === Number(optionTypeId)
    );
    let chosen = constraint;
    if (side === "offer" || side === "mid") {
      const sellFirst = variants.find((c) => String(c.buy_leg_one) === "0");
      if (sellFirst) chosen = sellFirst;
    }

    for (let i = 0; i < nLegs; i++) {
      const flags = legFlagsFromConstraint(chosen, i);
      if (!flags) continue;
      const strike =
        strikes[i] != null
          ? strikes[i]
          : strikes.length === 1
            ? strikes[0]
            : "";
      // Comma-joined bespoke tenor stays on every leg as one token.
      const tenor =
        tenors.length === 1
          ? tenors[0] || ""
          : tenors.length > 1
            ? tenors[i % tenors.length]
            : "";
      legs.push({
        strike: strike === "" ? strikes[0] || "" : strike,
        cp: flags.cp,
        bs: flags.bs,
        ratio: ratios[i] || "1",
        tenor,
      });
    }
    strikes = legs.map((l) => l.strike);
  } else if (legs.length && ratioParts?.length) {
    legs = legs.map((l, i) => ({
      ...l,
      ratio: ratioParts[i] || l.ratio || "1",
    }));
  }

  // Underlying already recorded in `missing` when live/UL was absent.

  const sides = { buy: false, sell: false };
  if (twoWay || (prices.length === 2 && !side)) {
    sides.buy = true;
    sides.sell = true;
  } else if (side === "offer") {
    sides.sell = true;
  } else {
    // bid, mid, or omitted — buy is the default; no amber warning.
    sides.buy = true;
  }

  const typeName = optionTypeName(grammarPack, optionTypeId);

  const ok =
    !!contract &&
    tenors.length > 0 &&
    !!label &&
    !!optionTypeId &&
    legs.length > 0 &&
    live != null &&
    !missing.includes("structure");

  return {
    ok,
    contract,
    tenors,
    label,
    optionTypeId,
    optionTypeName: typeName,
    strikes,
    ratio: legs.map((l) => l.ratio),
    underlying,
    legUnderlyings,
    live,
    sides,
    prices,
    vols,
    legs,
    assumed,
    missing: ok ? missing.filter((m) => m !== "structure") : missing,
    namingFamily,
    side,
  };
}
